import { useEffect, useMemo, useState } from "react";
import { Flame } from "lucide-react";
import { useCustom, useCustomMutation, useList, useTranslate } from "@refinedev/core";
import { HomeIcon, LocationSelector, type LocationSelectorMode, Modal, ShieldIcon } from "@fawkes/ui";

import { useModuleAvailability } from "../api/modules";
import { DEFAULT_WERKMODUS, useWerkmodus, type Werkmodus } from "./werkmodus";

// Branch (vestiging) record as returned by GET /api/vestigingen and /api/vestigingen/current.
export interface Vestiging {
  id: number;
  naam: string;
  naamKort: string;
  code: string | null;
  plaats: string | null;
  displayName: string;
  // Address (verblijf-wizard map default center) — the branch's own location, used to open the
  // found-location map on the shelter's neighbourhood. Any may be null.
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
}

/**
 * jve-vestiging: the real, data-driven branch selector in the sidebar — replaces the disabled stub.
 * The accessible set comes from GET /api/vestigingen (scoped to the signed-in user); the active
 * selection from GET /api/vestigingen/current. "Andere vestiging openen" opens a modal listing the
 * accessible vestigingen (the active one flagged); a search box narrows the list server-side via
 * GET /api/vestigingen?q=<term> (matching naam or woonplaats). Selecting one PUTs the new current
 * and re-renders the shell. Mirrors the Jmix MainView selector card + filterable switch combobox.
 *
 * {@link VestigingSwitcherProps.onSwitched} fires after the active vestiging actually changes, so the
 * host can leave the now-stale (vestiging-scoped) working area — the shell navigates home, mirroring
 * the Jmix MainView reload on a branch switch.
 */
export interface VestigingSwitcherProps {
  /** Called after the active vestiging successfully changes (not when re-selecting the current one). */
  onSwitched?: () => void;
  /**
   * Called after the werkmodus (Asiel / Pension) actually changes. The two administrations do not
   * share every screen — the animal list is asiel-only — so the host leaves the current screen.
   */
  onModusSwitched?: (modus: Werkmodus) => void;
}

export function VestigingSwitcher({ onSwitched, onModusSwitched }: VestigingSwitcherProps = {}) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  // Unfiltered: drives the sidebar card's accessibility/empty state only — kept independent of the
  // modal search so a non-matching search term never collapses the card to its disabled stub.
  const { data: listData, isLoading: listLoading, isError: listError } = useList<Vestiging>({
    resource: "vestigingen",
  });
  const {
    data: currentData,
    isLoading: currentLoading,
    refetch: refetchCurrent,
  } = useCustom<Vestiging>({ url: "vestigingen/current", method: "get" });

  const { mutate: switchVestiging, isLoading: switching } = useCustomMutation();

  // De werkmodussegmenten onder de vestiging — alleen de administraties die deze organisatie ook
  // organisation actually runs (GET /api/modules) get a segment, and the card drops the switch
  // altogether when just one of them is on.
  const { isModuleEnabled } = useModuleAvailability();
  const [werkmodus, setWerkmodus] = useWerkmodus();
  const modes: LocationSelectorMode[] = [
    { value: "asiel", label: t("vestigingSelector.modus.asiel"), icon: <ShieldIcon width={15} height={15} /> },
    { value: "pension", label: t("vestigingSelector.modus.pension"), icon: <HomeIcon width={15} height={15} /> },
    { value: "crematorium", label: t("vestigingSelector.modus.crematorium"), icon: <Flame size={15} aria-hidden="true" /> },
  ].filter((m) => isModuleEnabled(m.value));
  // A mode whose module was switched off must not stay selected — fall back to the first available.
  const activeModus = modes.some((m) => m.value === werkmodus) ? werkmodus : modes[0]?.value;

  // Debounce the search term (mirror RelatieList: 300 ms) before it reaches the server.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  // Server-side filtered list for the modal — fires only while the dialog is open. Relative path:
  // the dataProvider prepends "/api", so an absolute "/api/..." would double to "/api/api/...".
  const filterUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    const qs = params.toString();
    return qs ? `vestigingen?${qs}` : "vestigingen";
  }, [debounced]);
  const { data: filteredData, isFetching: filtering } = useCustom<Vestiging[]>({
    url: filterUrl,
    method: "get",
    queryOptions: { enabled: open, keepPreviousData: true },
  });

  const hasAccess = (listData?.data ?? []).length > 0;
  const results = filteredData?.data ?? [];
  const current = currentData?.data;
  const loading = listLoading || currentLoading;

  function openDialog() {
    setQuery("");
    setDebounced("");
    setOpen(true);
  }

  function select(v: Vestiging) {
    if (v.id === current?.id) {
      setOpen(false);
      return;
    }
    switchVestiging(
      // Relative path: the dataProvider prepends the API base ("/api"); passing an absolute
      // "/api/..." here would double it to "/api/api/..." (404).
      { url: "vestigingen/current", method: "put", values: { id: v.id } },
      {
        onSuccess: () => {
          setOpen(false);
          refetchCurrent();
          // Leave the now-stale vestiging-scoped screen (the host navigates home).
          onSwitched?.();
        },
      },
    );
  }

  // No accessible vestigingen (or a load failure): read-only card with the empty/placeholder text,
  // matching the Jmix "geen actieve vestigingen" disabled state.
  if (!loading && (listError || !hasAccess)) {
    return (
      <LocationSelector
        label={t("vestigingSelector.label")}
        name={t(listError ? "vestigingSelector.loadFailed" : "vestigingSelector.empty")}
        switchLabel={t("vestigingSelector.switchLabel")}
        disabled
      />
    );
  }

  const activeName = current?.displayName ?? (loading ? t("common.loading") : t("vestigingSelector.empty"));
  const activePlace = current?.plaats ?? undefined;

  return (
    <>
      <LocationSelector
        label={t("vestigingSelector.label")}
        name={activeName}
        place={activePlace}
        switchLabel={t("vestigingSelector.switchLabel")}
        onSwitch={loading ? undefined : openDialog}
        disabled={loading}
        modes={modes}
        activeMode={activeModus}
        onModeChange={(value) => {
          // Elke modus die als segment getekend is mag gekozen worden; een onbekende waarde valt
          // terug op de standaard in plaats van stilletjes niets te doen.
          const next: Werkmodus = modes.some((m) => m.value === value)
            ? (value as Werkmodus)
            : DEFAULT_WERKMODUS;
          if (next === activeModus) return;
          setWerkmodus(next);
          onModusSwitched?.(next);
        }}
        modesLabel={t("vestigingSelector.modus.label")}
      />
      <Modal open={open} title={t("vestigingSelector.dialogTitle")} onClose={() => setOpen(false)}>
        <p className="fawkes-muted fawkes-vestiging-picker__intro">
          {t("vestigingSelector.dialogIntro")}
        </p>
        <div className="fawkes-products-search fawkes-vestiging-picker__search">
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("vestigingSelector.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("vestigingSelector.searchPlaceholder")}
          />
        </div>
        <div className="fawkes-vestiging-picker__results">
        {results.length === 0 ? (
          <p className="fawkes-muted fawkes-vestiging-picker__empty">
            {filtering ? t("common.loading") : t("vestigingSelector.noResults")}
          </p>
        ) : (
          <ul className="fawkes-vestiging-picker" role="listbox" aria-label={t("vestigingSelector.label")}>
            {results.map((v) => {
              const isActive = v.id === current?.id;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    className="fawkes-vestiging-picker__item"
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive ? "" : undefined}
                    disabled={switching}
                    onClick={() => select(v)}
                  >
                    <span className="fawkes-vestiging-picker__text">
                      <span className="fawkes-vestiging-picker__name">{v.displayName}</span>
                      {v.plaats && <span className="fawkes-vestiging-picker__place">{v.plaats}</span>}
                    </span>
                    {isActive && (
                      <span className="fawkes-vestiging-picker__badge">{t("vestigingSelector.current")}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        </div>
      </Modal>
    </>
  );
}
